import React, { useState, useEffect } from 'react';
import { CircularProgress, Tabs, Tab, Box, Paper, Typography, Grid } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';
import { useAuth } from '../contexts/AuthContext';
import { getCareRecords } from '../services/firestoreService';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { ja } from 'date-fns/locale';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { BarChart, CalendarMonth } from '@mui/icons-material';
import { IconAvatar } from './IconSystem';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement
);

export default function Analytics({ refreshTrigger }) {
  const { currentUser } = useAuth();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(0); // 0: charts, 1: calendar
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarRecords, setCalendarRecords] = useState([]);
  const [periodType, setPeriodType] = useState('month'); // 'day', 'month', 'year'

  const loadData = async () => {
    setLoading(true);
    try {
      console.log('全記録を取得中:', { userId: currentUser.uid });
      const allRecords = await getCareRecords(currentUser.uid);
      console.log('取得した全記録:', allRecords);
      
      let filteredRecords;
      
      if (periodType === 'day') {
        // 日別フィルタリング
        const startDate = startOfMonth(new Date(selectedYear, selectedMonth - 1));
        const endDate = endOfMonth(startDate);
        filteredRecords = allRecords.filter(record => {
          const recordDate = record.timestamp;
          return recordDate >= startDate && recordDate <= endDate;
        });
      } else if (periodType === 'month') {
        // 月別フィルタリング（選択した年の全月）
        const startDate = new Date(selectedYear, 0, 1); // 1月1日
        const endDate = new Date(selectedYear, 11, 31, 23, 59, 59); // 12月31日
        filteredRecords = allRecords.filter(record => {
          const recordDate = record.timestamp;
          return recordDate >= startDate && recordDate <= endDate;
        });
      } else if (periodType === 'year') {
        // 年別フィルタリング（全記録）
        filteredRecords = allRecords;
      }
      
      console.log('フィルタリング後の記録:', filteredRecords);
      setRecords(filteredRecords);
    } catch (error) {
      console.error('統計データ読み込みエラー:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser) {
      loadData();
    }
  }, [currentUser, selectedYear, selectedMonth, periodType, refreshTrigger]);

  const loadAllRecords = async () => {
    setLoading(true);
    try {
      const allRecords = await getCareRecords(currentUser.uid);
      setCalendarRecords(allRecords);
    } catch (error) {
      console.error('全記録読み込みエラー:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentUser && viewMode === 1) {
      loadAllRecords();
    }
  }, [currentUser, viewMode]);

  const getRecordsForDate = (date) => {
    return calendarRecords.filter(record => isSameDay(record.timestamp, date));
  };

  const getTileContent = ({ date }) => {
    const dayRecords = getRecordsForDate(date);
    if (dayRecords.length > 0) {
      return (
        <div style={{
          position: 'absolute',
          bottom: '2px',
          right: '2px',
          backgroundColor: '#66BB6A',
          borderRadius: '50%',
          width: '8px',
          height: '8px',
          fontSize: '8px',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {dayRecords.length > 1 ? dayRecords.length : ''}
        </div>
      );
    }
    return null;
  };

  const selectedDateRecords = getRecordsForDate(selectedDate);

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) {
      return '時間未記録';
    }
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      return `${minutes}分`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}時間${remainingMinutes > 0 ? remainingMinutes + '分' : ''}`;
  };

  // タグランキングデータ
  const getTagRanking = () => {
    const tagCounts = {};
    const tagDurations = {};
    
    records.forEach(record => {
      record.tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
        if (record.duration && record.duration > 0) {
          tagDurations[tag] = (tagDurations[tag] || 0) + record.duration;
        }
      });
    });

    return Object.entries(tagCounts)
      .map(([tag, count]) => ({
        tag,
        count,
        totalDuration: tagDurations[tag] || 0,
        avgDuration: tagDurations[tag] && tagDurations[tag] > 0 ? Math.floor(tagDurations[tag] / count / 60) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const getPeriodTitle = () => {
    if (periodType === 'day') return `${selectedYear}年${selectedMonth}月の日別推移`;
    if (periodType === 'month') return `${selectedYear}年の月別推移`;
    if (periodType === 'year') return '年別推移';
  };

  // 期間別の記録数データ
  const getPeriodRecordsData = () => {
    if (periodType === 'day') {
      const startDate = startOfMonth(new Date(selectedYear, selectedMonth - 1));
      const endDate = endOfMonth(startDate);
      const daysInMonth = eachDayOfInterval({ start: startDate, end: endDate });
      
      const dailyCounts = {};
      daysInMonth.forEach(day => {
        dailyCounts[format(day, 'yyyy-MM-dd')] = 0;
      });
      
      records.forEach(record => {
        const dateKey = format(record.timestamp, 'yyyy-MM-dd');
        if (dailyCounts.hasOwnProperty(dateKey)) {
          dailyCounts[dateKey]++;
        }
      });

      return {
        labels: daysInMonth.map(day => format(day, 'd日')),
        datasets: [{
          label: '記録数',
          data: Object.values(dailyCounts),
          borderColor: 'rgb(102, 187, 106)',
          backgroundColor: 'rgba(102, 187, 106, 0.2)',
          tension: 0.1
        }]
      };
    } else if (periodType === 'month') {
      const monthlyCounts = {};
      for (let i = 0; i < 12; i++) {
        monthlyCounts[i + 1] = 0;
      }
      
      records.forEach(record => {
        const month = record.timestamp.getMonth() + 1;
        monthlyCounts[month]++;
      });

      return {
        labels: Array.from({length: 12}, (_, i) => `${i + 1}月`),
        datasets: [{
          label: '記録数',
          data: Object.values(monthlyCounts),
          borderColor: 'rgb(66, 165, 245)',
          backgroundColor: 'rgba(66, 165, 245, 0.2)',
          tension: 0.1
        }]
      };
    } else if (periodType === 'year') {
      const yearlyCounts = {};
      const currentYear = new Date().getFullYear();
      for (let year = currentYear - 4; year <= currentYear; year++) {
        yearlyCounts[year] = 0;
      }
      
      records.forEach(record => {
        const year = record.timestamp.getFullYear();
        if (yearlyCounts.hasOwnProperty(year)) {
          yearlyCounts[year]++;
        }
      });

      return {
        labels: Object.keys(yearlyCounts).map(year => `${year}年`),
        datasets: [{
          label: '記録数',
          data: Object.values(yearlyCounts),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.1
        }]
      };
    }
  };

  // 気分別の円グラフデータ
  const getFeelingDistribution = () => {
    const feelingCounts = {};
    records.forEach(record => {
      feelingCounts[record.feeling] = (feelingCounts[record.feeling] || 0) + 1;
    });

    const colors = [
      '#FF6384',
      '#36A2EB', 
      '#FFCE56',
      '#4BC0C0',
      '#9966FF',
      '#FF9F40'
    ];

    return {
      labels: Object.keys(feelingCounts),
      datasets: [{
        data: Object.values(feelingCounts),
        backgroundColor: colors.slice(0, Object.keys(feelingCounts).length),
        borderWidth: 2
      }]
    };
  };

  // コンテンツタイプ別の棒グラフデータ
  const getContentTypeData = () => {
    const typeCounts = {};
    records.forEach(record => {
      typeCounts[record.content_type] = (typeCounts[record.content_type] || 0) + 1;
    });

    return {
      labels: Object.keys(typeCounts),
      datasets: [{
        label: '記録数',
        data: Object.values(typeCounts),
        backgroundColor: 'rgba(66, 165, 245, 0.5)',
        borderColor: 'rgba(66, 165, 245, 1)',
        borderWidth: 1
      }]
    };
  };

  // 統計数値の計算
  const getStats = () => {
    const totalRecords = records.length;
    const recordsWithDuration = records.filter(record => record.duration && record.duration > 0);
    const totalDuration = recordsWithDuration.reduce((sum, record) => sum + record.duration, 0);
    const avgDuration = recordsWithDuration.length > 0 ? totalDuration / recordsWithDuration.length : 0;
    
    const uniqueTags = new Set();
    records.forEach(record => {
      record.tags.forEach(tag => uniqueTags.add(tag));
    });

    return {
      totalRecords,
      totalDuration: Math.floor(totalDuration / 60), // 分に変換
      avgDuration: Math.floor(avgDuration / 60), // 分に変換
      uniqueTags: uniqueTags.size,
      recordsWithDuration: recordsWithDuration.length
    };
  };

  const stats = getStats();
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
        ticks: {
          stepSize: 1,
          precision: 0
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <CircularProgress />
      </div>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconAvatar type="feeling" value="前向き" size={48} />
          <Typography variant="h5" sx={{ ml: 2, fontWeight: 700 }}>
            分析・統計
          </Typography>
        </Box>
        
        <Tabs
          value={viewMode}
          onChange={(e, newValue) => setViewMode(newValue)}
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 3,
              py: 1.5,
              borderRadius: '12px',
              mx: 0.5,
              fontWeight: 600,
              '&.Mui-selected': {
                background: 'linear-gradient(135deg, #66BB6A, #42A5F5)',
                color: 'white',
              },
            },
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab icon={<BarChart />} label="グラフ" />
          <Tab icon={<CalendarMonth />} label="カレンダー" />
        </Tabs>
      </Box>

      {viewMode === 1 ? (
        <Box>
          {/* カレンダービュー */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper 
                sx={{ 
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '& .react-calendar': {
                    width: '100%',
                    border: 'none',
                    borderRadius: '16px',
                    backgroundColor: 'transparent',
                    fontFamily: 'inherit',
                  },
                  '& .react-calendar__tile': {
                    position: 'relative',
                    border: 'none',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 187, 106, 0.2)',
                    },
                    '&--active': {
                      backgroundColor: '#66BB6A !important',
                      color: 'white !important',
                    },
                  },
                  '& .react-calendar__month-view__weekdays': {
                    textAlign: 'center',
                    textTransform: 'uppercase',
                    fontWeight: 'bold',
                    fontSize: '0.75em',
                    color: '#666',
                  },
                  '& .react-calendar__navigation button': {
                    border: 'none',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 187, 106, 0.2)',
                    },
                  },
                }}
              >
                <Calendar
                  onChange={setSelectedDate}
                  value={selectedDate}
                  tileContent={getTileContent}
                  locale="ja-JP"
                />
              </Paper>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Paper 
                sx={{ 
                  p: 3,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  {format(selectedDate, 'yyyy年MM月dd日', { locale: ja })}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  この日の記録: {selectedDateRecords.length}件
                </Typography>
                
                {selectedDateRecords.length === 0 ? (
                  <Box textAlign="center" py={4}>
                    <IconAvatar type="feeling" value="空虚" size={48} />
                    <Typography color="text.secondary" sx={{ mt: 2 }}>
                      この日は記録がありません
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {selectedDateRecords.map((record) => (
                      <Paper 
                        key={record.id}
                        sx={{ 
                          mb: 2,
                          p: 2,
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <Box display="flex" alignItems="center" mb={1}>
                          <IconAvatar type="feeling" value={record.feeling} size={24} />
                          <Typography variant="body2" sx={{ ml: 1, fontWeight: 600 }}>
                            {record.timeUnknown ? '時刻不明' : format(record.timestamp, 'HH:mm')}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {record.feeling} · {record.content_type}
                        </Typography>
                        <Typography variant="caption" color="primary.main">
                          {formatDuration(record.duration)}
                        </Typography>
                        {record.note && (
                          <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
                            {record.note}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box>
          {/* グラフビュー（既存のコードをMUIコンポーネントで置き換え） */}
          <Paper sx={{ p: 3, mb: 3, background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>Debug Information</Typography>
            <Typography variant="body2" color="text.secondary">選択期間: {selectedYear}年{selectedMonth}月</Typography>
            <Typography variant="body2" color="text.secondary">記録数: {records.length}件</Typography>
            <Typography variant="body2" color="text.secondary">ユーザーID: {currentUser?.uid}</Typography>
          </Paper>

          <Paper sx={{ p: 3, mb: 3, background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary" gutterBottom>表示単位</Typography>
                <select 
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'inherit'
                  }}
                >
                  <option value="day">日別</option>
                  <option value="month">月別</option>
                  <option value="year">年別</option>
                </select>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" color="text.secondary" gutterBottom>年</Typography>
                <select 
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  disabled={periodType === 'year'}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backgroundColor: periodType === 'year' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                    color: periodType === 'year' ? '#999' : 'inherit'
                  }}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}年</option>
                  ))}
                </select>
              </Grid>
              <Grid item xs={6} sm={4}>
                <Typography variant="body2" color="text.secondary" gutterBottom>月</Typography>
                <select 
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                  disabled={periodType !== 'day'}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    backgroundColor: periodType !== 'day' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                    color: periodType !== 'day' ? '#999' : 'inherit'
                  }}
                >
                  {months.map(month => (
                    <option key={month} value={month}>{month}月</option>
                  ))}
                </select>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #66BB6A, #42A5F5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {stats.totalRecords}
                </Typography>
                <Typography variant="body2" color="text.secondary">総記録数</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#42A5F5' }}>
                  {stats.totalDuration}
                </Typography>
                <Typography variant="body2" color="text.secondary">総時間（分）</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                  {stats.avgDuration}
                </Typography>
                <Typography variant="body2" color="text.secondary">平均時間（分）</Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 2, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#FF9800' }}>
                  {stats.uniqueTags}
                </Typography>
                <Typography variant="body2" color="text.secondary">ユニークタグ数</Typography>
              </Paper>
            </Grid>
          </Grid>

          {records.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
              <Typography variant="h3" sx={{ mb: 2 }}>📊</Typography>
              <Typography color="text.secondary" gutterBottom>
                選択した期間に記録がありません
              </Typography>
              <Typography variant="body2" color="text.secondary">
                記録を追加すると、ここに統計が表示されます
              </Typography>
            </Paper>
          ) : (
            <Box>
              <Paper sx={{ p: 3, mb: 3, background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>{getPeriodTitle()}</Typography>
                <Box sx={{ height: '320px' }}>
                  <Line data={getPeriodRecordsData()} options={chartOptions} />
                </Box>
              </Paper>

              <Grid container spacing={3}>
                <Grid item xs={12} lg={4}>
                  <Paper sx={{ p: 3, background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>気分・感情の分布</Typography>
                    <Box sx={{ height: '320px' }}>
                      <Pie data={getFeelingDistribution()} options={chartOptions} />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Paper sx={{ p: 3, background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>内容タイプ別記録数</Typography>
                    <Box sx={{ height: '320px' }}>
                      <Bar data={getContentTypeData()} options={chartOptions} />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} lg={4}>
                  <Paper sx={{ p: 3, background: 'rgba(255, 255, 255, 0.1)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>タグランキング TOP10</Typography>
                    <Box sx={{ maxHeight: '320px', overflowY: 'auto' }}>
                      {getTagRanking().length === 0 ? (
                        <Box textAlign="center" py={4}>
                          <Typography color="text.secondary">
                            タグがありません
                          </Typography>
                        </Box>
                      ) : (
                        getTagRanking().map((tagData, index) => (
                          <Box 
                            key={tagData.tag}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              p: 2,
                              mb: 1,
                              background: index < 3 
                                ? `linear-gradient(135deg, ${['#FFD700', '#C0C0C0', '#CD7F32'][index]}, ${['#FFA500', '#A8A8A8', '#A0522D'][index]})` 
                                : 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                            }}
                          >
                            <Box display="flex" alignItems="center">
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  mr: 2, 
                                  fontWeight: 700,
                                  color: index < 3 ? 'white' : 'inherit',
                                  textShadow: index < 3 ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none'
                                }}
                              >
                                #{index + 1}
                              </Typography>
                              <Box>
                                <Typography 
                                  variant="body1" 
                                  sx={{ 
                                    fontWeight: 600,
                                    color: index < 3 ? 'white' : 'inherit',
                                    textShadow: index < 3 ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none'
                                  }}
                                >
                                  {tagData.tag}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: index < 3 ? 'rgba(255,255,255,0.9)' : 'text.secondary'
                                  }}
                                >
                                  平均: {tagData.avgDuration > 0 ? `${tagData.avgDuration}分` : '時間未記録'}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 700,
                                color: index < 3 ? 'white' : 'primary.main',
                                textShadow: index < 3 ? '1px 1px 2px rgba(0,0,0,0.3)' : 'none'
                              }}
                            >
                              {tagData.count}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}